import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { MemoListRow } from '../../components/forms/MemoListRow';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import { fetchMemoSubmissions } from '../../lib/memoSubmissions';
import { formatMemoDateDisplay } from '../../lib/memoDocx';
import { STATUS_LABEL } from '../../lib/accountCreationUi';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import type { ApplicationSubmission, InternalMemoFormData } from '../../types/applicationForm';

function memoRowSearchBlob(row: ApplicationSubmission): string {
  const data = row.form_data as InternalMemoFormData;
  return [
    row.application_no,
    row.company_name,
    data?.subject,
    data?.to,
    data?.cc,
    data?.from_line,
    data?.date,
    formatMemoDateDisplay(data?.date ?? ''),
    data?.issued_by,
    row.submitted_by,
    row.status,
    STATUS_LABEL[row.status],
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}

export function InternalMemoListPage() {
  const { basePath } = usePortal();
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const listBase = basePath ? `${basePath}/internal-memo` : '/internal-memo';
  const newPath = `${listBase}/new`;
  const homePath = basePath || '/forms';

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchMemoSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load memoranda.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    tables: ['memo_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = rows.filter((row) => {
        const blob = memoRowSearchBlob(row);
        return tokens.every((token) => blob.includes(token));
      });
    }
    return [...list].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
  }, [rows, search]);

  const stats = useMemo(() => {
    let posted = 0;
    let saved = 0;
    let cancelled = 0;
    for (const row of rows) {
      if (row.status === 'posted') posted += 1;
      else if (row.status === 'saved') saved += 1;
      else if (row.status === 'cancelled') cancelled += 1;
    }
    return { total: rows.length, posted, saved, cancelled };
  }, [rows]);

  const detailPath = (id: string) => `${listBase}/${id}`;

  const countLabel = loading
    ? 'Loading…'
    : search.trim()
      ? `${filteredRows.length} of ${rows.length} memo${rows.length === 1 ? '' : 's'}`
      : `${filteredRows.length} memo${filteredRows.length === 1 ? '' : 's'}`;

  return (
    <div className="memo-page">
      <PageHeader
        title="Internal Memorandum"
        description="Draft and save internal memos — download Word or export PDF."
        actions={
          <div className="memo-page__header-actions">
            <Link to={homePath} className="btn-secondary">
              All forms
            </Link>
            <Link to={newPath} className="btn-primary">
              + New memo
            </Link>
          </div>
        }
      />

      <div className="kpi-grid dashboard-kpis">
        <div className="card kpi-card">
          <div className="kpi-label">Total memos</div>
          <div className="kpi-value">{stats.total}</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Posted</div>
          <div className="kpi-value">{stats.posted}</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Saved</div>
          <div className="kpi-value">{stats.saved}</div>
        </div>
        <div className="card kpi-card kpi-card--alert">
          <div className="kpi-label">Cancelled</div>
          <div className="kpi-value">{stats.cancelled}</div>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by reference, subject, recipient or sender"
        action={
          <div className="memo-page__toolbar-aside">
            <span className="page-count">{countLabel}</span>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={loading}
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
        }
      />

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading memoranda…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No memoranda yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            + New memo
          </Link>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <p>No memoranda match your search.</p>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="memo-list card">
          <div className="memo-list__head" role="row">
            <span>Ref no</span>
            <span>Subject</span>
            <span className="memo-list__head-col--wide">To</span>
            <span>From</span>
            <span>Date</span>
            <span>Status</span>
            <span>Saved</span>
            <span aria-hidden />
          </div>
          {filteredRows.map((row) => (
            <MemoListRow key={row.id} row={row} detailPath={detailPath(row.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
